import { useMemo } from 'react';
import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { StyleObj } from '../../../../@types';
import { BETSLIP_WIDTH } from '../../../../constants';
import { useReofferedBetslip } from '../../../../hooks/useReofferedBetslip';
import { useGroupedReofferedBets } from '../hooks/useGroupedReofferedBets';
import BetslipBetCount from '../../../atoms/BetslipBetCount';
import ReofferedBetslipCountdown from '../../../atoms/ReofferedBetslipCountdown';
import ReofferedBetslipActions from '../../../molecules/ReofferedBetslipActions';
import BetslipStakeInput from '../molecules/BetslipStakeInput';
import BetslipPlaceBetButton from '../molecules/BetslipPlaceBetButton';
import GolaReofferedBetslipItem from '../molecules/ReofferedBetslipItem';
import ReofferedBetBuilderItem from '../../../molecules/ReofferedBetBuilderItem';

import { mergeSx } from '../../../../utils/styles';

const styles: StyleObj = {
  drawer: {
    zIndex: 1400,
    maxHeight: '80dvh',
    textAlign: 'center',
    '& .MuiPaper-root': {
      width: {
        xs: '100%',
        sm: BETSLIP_WIDTH,
      },
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '80dvh',
      background: (theme) => theme.palette.neutral[50],
      borderRadius: {
        xs: 0,
        sm: '4px 4px 0 0',
      },
      margin: 'auto',
      mb: {
        xs: 0,
        md: 4,
      },
    },
  },
  header: {
    flexShrink: 0,
    height: 48,
    pr: 1,
  },
  navigationBtn: {
    p: 0,
    color: 'neutral.600',
  },
  betStack: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
      width: 14,
      borderColor: 'neutral.50',
    },
    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
      backgroundColor: 'neutral.300',
    },
  },
  inputRow: {
    flexShrink: 0,
  },
};

const GolaReofferedBetslipDrawer = () => {
  const {
    open,
    currentBetslip,
    betslip,
    bets,
    previousBets,
    betBuilderGroups,
    betsCount,
    currentIndex,
    totalSlips,
    isPrevDisabled,
    isNextDisabled,
    goToPrev,
    goToNext,
  } = useReofferedBetslip();

  const groupedBets = useGroupedReofferedBets(bets);

  const previousBetsMap = useMemo(
    () =>
      previousBets.reduce(
        (acc, bet) => {
          acc[bet.outcomeId] = bet;
          return acc;
        },
        {} as Record<string, (typeof previousBets)[number]>
      ),
    [previousBets]
  );

  const stakeDisplay = betslip?.totalStakeAmount?.toFixed(2) ?? '0.00';
  const maxPayout = betslip?.maxPayout ?? 0;
  const bonusPercentage = betslip?.bonus?.bonusPercentage ?? 0;

  return (
    <Drawer anchor='bottom' elevation={0} open={open} sx={styles.drawer}>
      <Stack
        direction='row'
        sx={mergeSx(
          {
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          styles.header
        )}
      >
        <BetslipBetCount
          numberOfBets={betsCount}
          sx={{
            pl: 2.5,
          }}
        />

        <Stack
          direction='row'
          spacing={0.5}
          sx={{
            alignItems: 'center',
            pr: 1,
          }}
        >
          {betslip?.createdAt && <ReofferedBetslipCountdown createdAt={betslip.createdAt} ticketId={currentBetslip} />}

          {totalSlips > 1 && (
            <Stack
              direction='row'
              sx={{
                alignItems: 'center',
              }}
            >
              <IconButton disabled={isPrevDisabled} sx={styles.navigationBtn} onClick={goToPrev}>
                <NavigateBefore />
              </IconButton>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 700,
                  color: 'neutral.600',
                }}
              >
                {currentIndex + 1}/{totalSlips}
              </Typography>
              <IconButton disabled={isNextDisabled} sx={styles.navigationBtn} onClick={goToNext}>
                <NavigateNext />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Stack sx={styles.betStack}>
        {groupedBets.map((betGroup, index) => (
          <GolaReofferedBetslipItem
            key={`reoffered-group-${index}`}
            bets={betGroup}
            previousBetsMap={previousBetsMap}
          />
        ))}
        {betBuilderGroups.map((group) => (
          <ReofferedBetBuilderItem key={group.id} group={group} />
        ))}
      </Stack>

      <Stack direction='row' sx={styles.inputRow}>
        <BetslipStakeInput value={stakeDisplay} disabled />
        <BetslipPlaceBetButton
          totalStakeAmount={betslip?.totalStakeAmount ?? 0}
          maxPayout={maxPayout}
          isDisabled
          isLoading={false}
          bonusPercentage={bonusPercentage}
        />
      </Stack>

      <ReofferedBetslipActions currentBetslip={currentBetslip} />
    </Drawer>
  );
};

export default GolaReofferedBetslipDrawer;
